import { ResponsiveBar, BarTooltipProps } from "@nivo/bar";
import React from "react";
import { graphPaletteV2, lightGreyText } from "../../../styles";
import {
  ExposureBarItem,
  FormattedExposureData,
} from "../../../models/stewardshipModels";
import { buildExposureBarsData } from "../utils";
import { formatNumber } from "../../../utils/formattingUtils";
import { FlexibleContainer } from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { generatePaletteColors } from "../../../utils/graphUtils";

const theme = {
  axis: {
    ticks: {
      text: {
        fill: lightGreyText,
        fontSize: 12,
        fontFamily: "Apercu",
      },
    },
  },
  grid: {
    line: {
      stroke: "#d3d3d3",
      strokeWidth: 0.4,
      strokeDasharray: "6 8",
    },
  },
};

const customTooltip = ({
  id,
  color,
  data,
}: BarTooltipProps<ExposureBarItem>): JSX.Element => {
  return (
    <div
      style={{
        background: "white",
        padding: "9px 12px",
        border: "1px solid #ccc",
        borderRadius: "7px",
      }}
    >
      <FlexibleContainer>
        <CompassParagraph bold={true}>Fund:</CompassParagraph>
        <LegendCircle color={color} />
        <CompassParagraph>{id}</CompassParagraph>
      </FlexibleContainer>
      <CompassParagraph>
        <strong>Exposure:</strong> {formatNumber((data[id] || 0) as number)}
      </CompassParagraph>
    </div>
  );
};

export const CompanyExposureBars: React.FC<MilestoneProgressBarsProps> = ({
  data,
}): JSX.Element => {
  const chartData: ExposureBarItem[] = buildExposureBarsData(data);

  return (
    <div style={{ height: "350px", width: "100%" }}>
      <ResponsiveBar
        data={chartData}
        keys={data.map((item: FormattedExposureData) => item.id)}
        indexBy="date"
        margin={{ top: 25, right: 50, bottom: 50, left: 70 }}
        padding={0.75}
        valueScale={{ type: "linear" }}
        indexScale={{ type: "band", round: true }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 5,
          tickRotation: 0,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 5,
          tickRotation: 0,
          legendPosition: "middle",
          format: (value) =>
            value >= 1000000 ? formatNumber(value, "M") : formatNumber(value),
        }}
        colors={generatePaletteColors(data, graphPaletteV2)}
        theme={theme}
        borderRadius={0}
        borderWidth={0}
        borderColor={{ from: "color", modifiers: [["darker", 1.6]] }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
        animate={true}
        enableLabel={false}
        groupMode="stacked"
        tooltip={customTooltip}
      />
    </div>
  );
};

type MilestoneProgressBarsProps = {
  data: FormattedExposureData[];
};
